/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React from 'react';
import loadable from '@loadable/component';
import { withRouter } from 'react-router';
import {
    object, shape, string, bool, array, func, number, arrayOf,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import track from 'react-tracking';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Grid from '@material-ui/core/Grid';
import { getFeatureFlags } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import * as TagManagerActions from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { trackPage, trackProductData, trackCYOData } from '../../../../../../helpers/tracking/common/commonTrackingHelpers';
import { addOgDynamicData, getBreadcrumbArray } from '../../common/commonProductPageHelper';
import { addToCartDataType, deliveryDataType, addonGroupsType } from '../../common/types';
import { setSelectedBYOItems } from '../../../../../../../state/ducks/App/App-Actions';
import { getSelectedBYOItems } from '../../../../../../../state/ducks/App/App-Selectors';
import { getActiveABTests } from '../../../../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';

const LoadableCYOProductItems = loadable(() => import(/* webpackChunkName: "CYOProductItemsV1" */ './v1/CYOProductItems'));
const LoadableCYOProductItemsV2 = loadable(() => import(/* webpackChunkName: "CYOProductItemsV2" */ './v2/CYOProductItemsVTwo'));
const LoadableCYOProductDetails = loadable(() => import(/* webpackChunkName: "CYOProductDetailsV1" */ './v1/CYOProductDetails'));
const LoadableCYOProductDetailsV2 = loadable(() => import(/* webpackChunkName: "CYOProductDetailsV2" */ './v2/CYOProductDetailsVTwo'));
const LoadableBYOProductPage = loadable(() => import(/* webpackChunkName: "BYOProductPage" */ './BYOProductPage/BYOProductPage'));
const LoadableGraphqlProductSeoSchema = loadable(() => import(/* webpackChunkName: "GraphqlProductSeoSchema" */ '../../GraphqlProductSeoSchema/GraphqlProductSeoSchema'));
const LoadablePDPSkeleton = loadable(() => import(/* webpackChunkName: "PDPSkeleton" */ '../../../../GraphqlSkeletonComponents/PDPSkeleton'));
const LoadableCommonProductAgeZipVerify = loadable(() => import(/* webpackChunkName: "CommonProductAgeZipVerify" */ '../../CommonProductAgeZipVerify/CommonProductAgeZipVerify'));
const LoadableGraphqlAddonContainer = loadable(() => import(/* webpackChunkName: "GraphqlAddonContainer" */ '../../GraphqlAddon/GraphqlAddonContainer'));

// TODO: Move into CMS
const USE_GUIDED = false;

const styles = (theme) => ({
    root: {
        margin: '0 auto',
        maxWidth: '1380px',
        padding: '10px 0px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            padding: '0',
        },
    },
    breadCrumbBody: {
        display: 'flex',
        margin: '30px auto 10px auto',
        paddingLeft: theme.spacing(2),
        boxSizing: 'border-box',
        maxWidth: 1380,
    },
    zipCodeInputValid: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            // margin: '25px 0px 0px 50px',
            backgroundColor: theme.palette.colorValid,
            color: theme.palette.white,
            textAlign: 'center',
            content: '"✓"',
            margin: '-60px 0px 0px 95px',
            fontSize: '16px',
            padding: '5px',
            fontWeight: 'bold',
            lineHeight: '17px',
            width: '25px',
            height: '25px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    zipCodeInputInvalidIcon: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            backgroundColor: theme.palette.cms?.errorZipVerifyPDP,
            color: theme.palette.white,
            textAlign: 'center',
            content: '"X"',
            fontSize: '17px',
            padding: '5px',
            fontWeight: 'bold',
            margin: '-32px 0px 0px 96px',
            lineHeight: '17px',
            width: '30px',
            height: '30px',
            top: 0,
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    zipInputTextValid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.colorValid,
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"ZIP code confirmed eligible "',
            margin: '10px 0px 0px -100px',
            border: 'none',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    zipCodeInputInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP,
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"Not eligible for Wine shipment"',
            bottom: '-34px',
            left: 0,
            paddingLeft: '0',
            width: '250px',
            height: '25px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInput: {
        width: '100%',
        display: 'flex',  // IE 11
        padding: '8px 0px 8px 0px',
        '& > div': {
            width: '100%', // IE 11
        },
    },
    ageInputInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP,
            lineHeight: '25px',
            fontSize: '12px',
            border: 'none',
            fontWeight: '200',
            content: '"You must be 21 or older to purchase wine!"',
            height: '22px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    dateInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP,
            lineHeight: '25px',
            fontSize: '12px',
            border: 'none',
            fontWeight: '200',
            margin: '0px 0px 0px 20px',
            content: '"Please enter a valid date of birth !"',
            height: '22px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInputInvalidIcon: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            margin: '-50px 0px 0px 205px',
            backgroundColor: theme.palette.cms?.errorZipVerifyPDP,
            color: theme.palette.white,
            textAlign: 'center',
            content: '\'\\2715\'',
            fontSize: '20px',
            padding: '3px',
            fontWeight: 'bold',
            lineHeight: '17px',
            left: '7px',
            width: '25px',
            height: '25px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    grid: {
        width: '250px',
        height: '90px',
    },
    ageInputTextValid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.colorValid,
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"Age Confirmed"',
            width: '95px',
            margin: '0px 0px 0px 12px',
            border: 'none',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInputValid: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            backgroundColor: theme.palette.colorValid,
            color: theme.palette.white,
            textAlign: 'center',
            content: '"✓"',
            margin: '-50px 0px 0px 215px',
            fontSize: '16px',
            padding: '7px',
            fontWeight: 'bold',
            lineHeight: '17px',
            width: '25px',
            height: '25px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    errorMessage: {
        color: theme.palette.error?.main,
        padding: '5px 20px',
    },
});

const notCountableItemsGroup = ['pyo food'];
const nonCountableTypeName = 'cyoNonCountable';
const hideAndAutoSelect = true;

class GraphqlCYOProductPageBuilder extends React.Component {
    constructor(props) {
        super(props);

        // the SKUs can come back in an unpredictable order but they should either be displayed highest price to lowest
        // or lowest to highest which will be set by product in CMS. The following lines sort the SKUs based on that CMS data
        // .slice is used as to not modify props
        const { content, product } = this.props;
        const productSkus = product ? product.productSkus : null;
        const sku_ranking = content?.entries?.[0]?.sku_section?.sku_ranking;
        if (productSkus) {
            if (sku_ranking === 'low to high') {
                this.sortedData = productSkus
                    .slice(0)
                    .sort(
                        (a, b) => Number(a.prices?.[0]?.value) - Number(b.prices?.[0]?.value),
                    );
            } else {
                this.sortedData = productSkus
                    .slice(0)
                    .sort(
                        (a, b) => Number(b.prices?.[0]?.value) - Number(a.prices?.[0]?.value),
                    );
            }
            this.state = {
                selectedSku: this.sortedData[0], forcedIndex: false, selectedComponent: [], fireImpression: true,
            };
        }
    }

    componentDidUpdate() {
        const { trackEvent, featureFlags } = this.props;
        if (featureFlags['experiment-byo-redesign'] && this.state.fireImpression) {
            if (featureFlags['is-byo-pdp-enabled'] && featureFlags['experiment-byo-redesign'] === 'variant') {
            // Not using tracking helper because this is Class component.
                trackEvent({
                    eventCategory: 'Experiments',
                    eventAction: 'BYO PDP - Impression',
                    eventLabel: 'Variant',
                    nonInteraction: true,
                    queued: !!(typeof window !== 'undefined' && !window.utag?.data),
                });
            } else {
                trackEvent({
                    eventCategory: 'Test Impression',
                    eventAction: `CYO Design - ${!featureFlags['is-cyo-v2-enabled'] && 'Control'}`,
                    eventLabel: '',
                    queued: !!(typeof window !== 'undefined' && !window.utag?.data),
                });
                trackEvent({
                    eventCategory: 'Experiments',
                    eventAction: 'CYO PDP - Impression',
                    eventLabel: 'Control',
                    nonInteraction: true,
                    queued: !!(typeof window !== 'undefined' && !window.utag?.data),
                });
            }
            this.setState({ fireImpression: false });
        }
    }

    handleSkuChange = (e, emptyArg = '', dynamicPriceData) => {
        if ((!e || !e.target?.value) && dynamicPriceData !== null) {
            this.setState((prevState) => ({
                selectedSku: {
                    ...prevState.selectedSku,
                    dynamicPrice: dynamicPriceData,
                },
            }));
            return;
        }

        const { selectedBYOItems, selectedItems } = this.props;
        const selectedSku =  this.sortedData.find((sku) => sku.id === e.target.value);
        this.setState({
            selectedSku: this.sortedData.find((sku) => sku.id === e.target.value),
        });
        selectedBYOItems({
            ...selectedItems,
            selectedSku,
        });
    };

    setSelectedComponents = (components) => {
        this.setState({ selectedComponent: components });
    }

    handleMakeSelection = () => {
        this.setState({
            forcedIndex: 0,
        });
        if (window) {
            // We need to set back to false so the button will work multiple times
            setTimeout(() => this.setState({ forcedIndex: false }), 1000);
        }
    };

    showErrorMessage = (justify) => {
        const { classes } = this.props;
        return (
            <Grid container space={2} justify={justify || 'flex-end'} className={classes.errorMessage}>
                <Grid xs={8}>Sorry, there are currently no available options to select for this product.</Grid>
            </Grid>
        );
    };

    getQueryParam = () => {
        const { urlParams } = this.props;
        const paramKeys = Object.keys(urlParams);
        if (paramKeys.length) {
            let queryStr = '';
            paramKeys.forEach((key) => {
                switch (key) {
                    case 'r':
                    case 'ref':
                    case 'rel':
                        queryStr = '?';
                        queryStr += `${queryStr.length > 1 ? '&' : ''}${key}=${urlParams[key]}`;
                        break;
                    default:
                        break;
                }
            });
            return queryStr;
        }
        return '';
    };

    renderProductDetails = ({
        content,
        product,
        selectedSku,
        location,
        presentation_family,
        brand,
        passportMemberImg,
        isPassportBundleProduct,
        url,
        productBaseContent,
        featureFlags,
    }) => (featureFlags['is-cyo-v2-enabled'] ? (
        <LoadableCYOProductDetailsV2
            content={content}
            data={product}
            selectedSku={selectedSku}
            location={location}
            skuData={this.sortedData}
            handleSkuChange={this.handleSkuChange}
            handleMakeSelection={this.handleMakeSelection}
            presentation_family={presentation_family}
            brand={brand}
            passportMemberImg={passportMemberImg}
            isPassportBundleProduct={isPassportBundleProduct}
            url={url}
            productBaseContent={productBaseContent}
        />
    ) : (
        <LoadableCYOProductDetails
            content={content}
            data={product}
            selectedSku={selectedSku}
            location={location}
            skuData={this.sortedData}
            handleSkuChange={this.handleSkuChange}
            presentation_family={presentation_family}
            brand={brand}
            productBaseContent={productBaseContent}
        />
    ))

    renderProductItems = ({
        addonContainer,
        handleMiniCartModalClick,
        selectedSku,
        brandId,
        product,
        cartError,
        hasWine,
        handleMobileAgeVerification,
        trackEvent,
        location,
        deliverySLA,
        selectedAddons,
        forcedIndex,
        featureFlags,
        url,
        hasAddons,
        presentation_family,

    }) => (featureFlags['is-cyo-v2-enabled'] ? (
        <div>
            <LoadableCYOProductItemsV2
                handleMiniCartModalClick={handleMiniCartModalClick}
                cyoData={selectedSku.cyo}
                selectedItemId={selectedSku.id}
                brandId={brandId}
                product={product}
                cartError={cartError}
                hasWine={hasWine}
                handleMobileAgeVerification={handleMobileAgeVerification}
                nonCountableKey={nonCountableTypeName}
                track={trackEvent}
                categoryData={{
                    categoryId: location?.state?.categoryId,
                    categoryName: location?.state?.categoryName,
                    categoryPath: location?.state?.categoryPath,
                }}
                deliverySLA={deliverySLA}
                selectedAddons={selectedAddons?.cart || []}
                showErrorMessage={this.showErrorMessage}
                hideMenu={!selectedSku.cyo?.cyoGroup || selectedSku.cyo?.cyoGroup.length <= 2}
                useGuided={USE_GUIDED}
                forcedIndex={forcedIndex}
                featureFlags={featureFlags}
                url={url}
                addonContainer={addonContainer}
                hasAddons={hasAddons}
                setSelectedComponents={this.setSelectedComponents}
                presentationFamily={presentation_family}
            />
        </div>
    ) : (
        <div>
            <LoadableCYOProductItems
                handleMiniCartModalClick={handleMiniCartModalClick}
                cyoData={selectedSku.cyo}
                selectedItemId={selectedSku.id}
                brandId={brandId}
                product={product}
                cartError={cartError}
                hasWine={hasWine}
                handleMobileAgeVerification={handleMobileAgeVerification}
                nonCountableKey={nonCountableTypeName}
                track={trackEvent}
                categoryData={{
                    categoryId: location?.state?.categoryId,
                    categoryName: location?.state?.categoryName,
                    categoryPath: location?.state?.categoryPath,
                }}
                deliverySLA={deliverySLA}
                selectedAddons={selectedAddons?.cart || []}
                showErrorMessage={this.showErrorMessage}
                setSelectedComponents={this.setSelectedComponents}
            />
        </div>
    ));

    // Track Product
    @track(trackProductData)
    @track(trackCYOData)
    render() {
        const {
            classes,
            seoData,
            product,
            location,
            presentation_family,
            brand,
            content,
            handleMiniCartModalClick,
            cartError,
            hasWine,
            mobileAgeVerificationOpen,
            handleMobileAgeVerification,
            addToCartData,
            deliveryData,
            updateWineStatus,
            trackEvent,
            deliverySLA,
            hasAddons,
            sortedData,
            selectedAddons,
            handleAddonChange,
            handleRemoveAddon,
            subscription,
            subscriptionDetails,
            disableAddToCart,
            isAddToCartEnabled,
            addonGroups,
            productBaseContent,
            passportMemberImg,
            isPassportBundleProduct,
            featureFlags,
        } = this.props;

        if (product) {
            const {
                partNumber, shortDescription, name, productSkus, brandId, reviews, productReviews,
            } = product;
            const { selectedSku, forcedIndex, selectedComponent } = this.state;
            const url = `${product.seo?.url}${this.getQueryParam()}` || '';

            // Because we may need to modify a property of selectedSku, which comes from Apollo, hold new object here
            let newSelectedSku = selectedSku;

            if (selectedSku?.cyo?.cyoGroup && selectedSku.cyo.cyoGroup.length > 0) {
                // Make a clone so that we can modify cyoGroup
                newSelectedSku = {
                    ...selectedSku,
                    cyo: {
                        ...selectedSku.cyo,
                        cyoGroup: (selectedSku.cyo?.cyoGroup || []).map((group) => {
                            const overrideGroup = { ...group };
                            if (notCountableItemsGroup.includes(group?.name?.toLowerCase() || '')) {
                                overrideGroup.type = nonCountableTypeName;
                                if (group?.components?.length === 1 && hideAndAutoSelect) {
                                    overrideGroup.hide = true;
                                }
                            }
                            return overrideGroup;
                        }),
                    },
                };
            }

            const addonContainer = () => hasAddons && (
                <div id="add-on-container">
                    <LoadableGraphqlAddonContainer
                        selectedAddons={selectedAddons?.list || []}
                        handleAddonAdd={handleAddonChange}
                        product={product}
                        skuData={sortedData}
                        brand={{ domain: product.brand || brand.domain }}
                        handleMiniCartModalClick={handleMiniCartModalClick}
                        brandId={brandId}
                        partNumber={partNumber}
                        categoryId={location.state?.categoryId || ''}
                        subscription={subscription}
                        subscriptionDetails={subscriptionDetails}
                        presentation_family={presentation_family}
                        disableAddToCart={disableAddToCart}
                        isAddToCartEnabled={isAddToCartEnabled}
                        categoryPath={location.state?.categoryPath || ''}
                        categoryName={location.state?.categoryName || ''}
                        selectedSku={newSelectedSku}
                        productSkus={productSkus}
                        updateWineStatus={updateWineStatus}
                        deliveryData={deliveryData}
                        mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                        handleMobileAgeVerification={handleMobileAgeVerification}
                        addToCartData={addToCartData}
                        addonGroups={addonGroups}
                        mixMatch
                        addonList={selectedAddons}
                        handleRemoveAddon={handleRemoveAddon}
                        selectedMixMatchComponent={selectedComponent}
                    />
                </div>
            );
            return (
                <>
                    {addOgDynamicData(product, seoData, brand, location)}
                    <LoadableGraphqlProductSeoSchema
                        name={name}
                        shortDescription={shortDescription}
                        partNumber={partNumber}
                        productSkus={productSkus}
                        location={location}
                        brand={brand}
                        reviews={reviews}
                        productReviews={productReviews}
                    />
                    <div className={classes.root}>
                        {hasWine
                            ? (
                                <LoadableCommonProductAgeZipVerify
                                    classes={classes}
                                    updateWineStatus={updateWineStatus}
                                    selectedSku={newSelectedSku}
                                    deliveryData={deliveryData}
                                    productSkus={newSelectedSku}
                                    brand={brand}
                                    mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                                    handleMobileAgeVerification={handleMobileAgeVerification}
                                    addToCartData={addToCartData}
                                    modal
                                    featureFlags={featureFlags}
                                    forceModal
                                    components={selectedComponent}
                                />
                            )
                            : null}
                        <div className={classes.breadCrumbBody}>{getBreadcrumbArray(product, location)}</div>
                        {featureFlags['is-byo-pdp-enabled'] && featureFlags['experiment-byo-redesign'] === 'variant'
                            ? (
                                <LoadableBYOProductPage
                                    skuData={this.sortedData}
                                    content={content}
                                    data={product}
                                    selectedSku={newSelectedSku}
                                    location={location}
                                    handleSkuChange={this.handleSkuChange}
                                    handleMakeSelection={this.handleMakeSelection}
                                    presentation_family={presentation_family}
                                    brand={brand}
                                    passportMemberImg={passportMemberImg}
                                    isPassportBundleProduct={isPassportBundleProduct}
                                    url={url}
                                    productBaseContent={productBaseContent}
                                    handleMiniCartModalClick={handleMiniCartModalClick}
                                    cyoData={selectedSku.cyo}
                                    selectedItemId={selectedSku.id}
                                    brandId={brandId}
                                    product={product}
                                    cartError={cartError}
                                    hasWine={hasWine}
                                    addonContainer={addonContainer}
                                    handleMobileAgeVerification={handleMobileAgeVerification}
                                    nonCountableKey={nonCountableTypeName}
                                    track={trackEvent}
                                    categoryData={{
                                        categoryId: location?.state?.categoryId,
                                        categoryName: location?.state?.categoryName,
                                        categoryPath: location?.state?.categoryPath,
                                    }}
                                    deliverySLA={deliverySLA}
                                    selectedAddons={selectedAddons?.cart || []}
                                    showErrorMessage={this.showErrorMessage}
                                    hideMenu={!selectedSku.cyo?.cyoGroup || selectedSku.cyo?.cyoGroup.length <= 2}
                                    useGuided={USE_GUIDED}
                                    forcedIndex={forcedIndex}
                                    featureFlags={featureFlags}
                                    hasAddons={hasAddons}
                                    setSelectedComponents={this.setSelectedComponents}
                                />
                            )
                            : (
                                <div>
                                    {this.renderProductDetails({
                                        content,
                                        product,
                                        selectedSku: newSelectedSku,
                                        location,
                                        presentation_family,
                                        brand,
                                        passportMemberImg,
                                        isPassportBundleProduct,
                                        url,
                                        productBaseContent,
                                        featureFlags,
                                    })}
                                    {this.renderProductItems({
                                        addonContainer,
                                        handleMiniCartModalClick,
                                        selectedSku: newSelectedSku,
                                        brandId,
                                        product,
                                        cartError,
                                        hasWine,
                                        handleMobileAgeVerification,
                                        trackEvent,
                                        location,
                                        deliverySLA,
                                        selectedAddons,
                                        forcedIndex,
                                        featureFlags,
                                        url,
                                        hasAddons,
                                    })}
                                </div>
                            )}
                    </div>
                </>
            );
        }
        return <LoadablePDPSkeleton />;
    }
}
GraphqlCYOProductPageBuilder.propTypes = {
    classes: object.isRequired,
    content: object.isRequired,
    product: object.isRequired,
    location: object.isRequired,
    handleMiniCartModalClick: func.isRequired,
    seoData: shape({
        seo_searchable: bool,
        page_title: string,
        keywords: string,
        meta_description: string,
        og: array,
    }).isRequired,
    presentation_family: string.isRequired,
    brand: object.isRequired,
    cartError: string.isRequired,
    urlParams: object.isRequired,
    passportMemberImg: shape({
        url: string,
        title: string,
    }),
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    hasWine: bool,
    updateWineStatus: func,
    mobileAgeVerificationOpen: bool,
    handleMobileAgeVerification: func,
    addToCartData: addToCartDataType,
    deliveryData: deliveryDataType,
    trackEvent: func.isRequired,
    deliverySLA: string.isRequired,
    featureFlags: object.isRequired,
    hasAddons: bool,
    sortedData: array,
    selectedAddons: shape({
        cart: arrayOf(shape({
            brandCode: string,
            productCode: string,
            componentType: string,
            productId: string,
            quantity: number,
        })),
        list: arrayOf(shape({
            id: string,
            ageVerifyFlag: bool,
            partNumber: string,
            name: string,
        })),
    }),
    handleAddonChange: func,
    handleRemoveAddon: func,
    subscription: string,
    subscriptionDetails: string,
    disableAddToCart: bool,
    isAddToCartEnabled: bool,
    addonGroups: addonGroupsType,
    productBaseContent: shape({
        entries: arrayOf(shape({
            sku_section: shape({
                show_sku_on_mobile: bool,
            }),
        })),
    }),
    selectedBYOItems: func,
    selectedItems: object,
};

GraphqlCYOProductPageBuilder.defaultProps = {
    hasWine: false,
    updateWineStatus: () => { },
    mobileAgeVerificationOpen: false,
    handleMobileAgeVerification: () => { },
    addToCartData: {},
    deliveryData: {},
    hasAddons: false,
    passportMemberImg: {},
    isPassportBundleProduct: {},
    sortedData: [],
    selectedAddons: {
        cart: [],
        list: [],
    },
    handleAddonChange: () => { },
    handleRemoveAddon: () => { },
    subscription: '',
    subscriptionDetails: '',
    disableAddToCart: false,
    isAddToCartEnabled: true,
    addonGroups: [],
    productBaseContent: {},
    selectedBYOItems: () => {},
    selectedItems: {},
};

const mapStateToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
    selectedItems: getSelectedBYOItems(state),
    activeABTests: getActiveABTests(state),

});

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(TagManagerActions.trackEvent, dispatch),
    selectedBYOItems: bindActionCreators(setSelectedBYOItems, dispatch),
});

const enhance = compose(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
    trackPage(),
    withRouter,
    withStyles(styles),
);
export default enhance(GraphqlCYOProductPageBuilder);
